import RoomResult from './RoomResult';
import { CalendarEventTime, Room, RoomSelectorFilters } from './RoomSelector.types';
import getRoomlistCapabilities from '../../selectors/getRoomlistCapabilities';
import getStore from '../../store/store';
import getRoomFreeBusy from '../../utils/getRoomFreebusy';
import { CapabilityFilters } from '../RoomFilter/CapabilityFilters';
import CapabilityFilter, { CapabilityFilterType } from '../RoomFilter/CapabilityFilterType';
import { FeatureFilterItemType } from '../RoomFilter/FeatureFilterItemType';
import { removeCapacitySuffixIfNeeded } from '../RoomFilter/utils/getAdjustedStringUtils';
import { getId } from '@uifabric/utilities';
import { observer } from 'mobx-react-lite';
import { FocusZone, FocusZoneDirection, Label } from '@fluentui/react/lib';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { getOwaResourceUrl } from 'owa-resource-url';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FreeBusyCacheManager } from 'owa-freebusy-cache';
import loc, { format } from 'owa-localize';
import * as React from 'react';
import EmptyState from 'owa-empty-state/lib/components/EmptyState';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import {
    roomPanelShowingAvailableRoomsIn,
    roomPanelShowingRoomsIn,
    roomPanelSuggestedRooms,
    noRoomsFoundMessageLineOne,
    noRoomsFoundMessageLineTwo,
    placeTypeSpaces,
    placeTypeConferenceRooms,
    roomSelectionAnnouncement,
} from './RoomResultsList.locstring.json';
/* tslint:disable:jsx-no-lambda WI:47594 */
import {
    hasSelectedFromSearch,
    hasSelectedFromSuggestion,
} from '../../mutators/roomPanelMetricsMutators';
import {
    updateCapacityValue,
    updateFeaturesValue,
    updateTagsValue,
    updateFloorValue,
} from '../../mutators/capabilityFilterMutators';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { LiveNotification } from 'owa-live-notification';

export interface RoomResultsListProps {
    calendarEvent: CalendarEventTime;
    colorScheme: ColorScheme;
    freeBusyCacheManager: FreeBusyCacheManager;
    onRoomSelected: (room: Room) => void;
    roomFilters: RoomSelectorFilters;
}

const styles = {
    base: require('./RoomResultsList.scss'),
    desktop: require('./RoomResultsList.outlookdesktop.scss'),
    mac: require('./RoomResultsList.macoutlook.scss'),
};

const classNames = require('classnames');

/**
 * Displays a list of room results
 */
export default observer(function RoomResultsList(props: RoomResultsListProps) {
    const { getClass } = useClientStyles(styles);
    const flights = {
        advancedRoomBooking: isFeatureEnabled('cal-advancedRoomBooking'),
        advancedRoomBookingV2: isFeatureEnabled('cal-mf-advancedRoomBookingV2'),
    };

    const [selectedRoomName, setSelectedRoomName] = React.useState<string | null>(null);

    const roomResultsHeaderLabelId = React.useRef<string>(
        flights.advancedRoomBooking ? getId('roomResultsHeaderLabel') : undefined
    );

    const locPlaceType = loc(
        getStore().roomFilterState.shouldGetSpaces ? placeTypeSpaces : placeTypeConferenceRooms
    );

    const renderHeader = (): JSX.Element => {
        let roomResultsHeader: string = '';
        let filterDisplayNames: string[] = [];
        let { selectedOption } = getStore().roomListPickerViewState;

        if (props.roomFilters.cities && props.roomFilters.cities.length > 0) {
            filterDisplayNames = filterDisplayNames.concat(props.roomFilters.cities);
        }
        if (props.roomFilters.roomLists && props.roomFilters.roomLists.length > 0) {
            filterDisplayNames = filterDisplayNames.concat(
                props.roomFilters.roomLists.map(x => x.name)
            );
        }
        if (filterDisplayNames.length === 0) {
            if (selectedOption) {
                roomResultsHeader = props.roomFilters.showOnlyAvailableRooms
                    ? format(
                          loc(roomPanelShowingAvailableRoomsIn),
                          locPlaceType,
                          selectedOption.displayName
                      )
                    : format(
                          loc(roomPanelShowingRoomsIn),
                          locPlaceType,
                          selectedOption.displayName
                      );
            } else {
                roomResultsHeader = format(
                    loc(roomPanelSuggestedRooms),
                    locPlaceType.toLocaleLowerCase()
                );
            }
        } else if (flights.advancedRoomBookingV2) {
            const appliedFilters = filterDisplayNames.join(', ');
            roomResultsHeader = props.roomFilters.showOnlyAvailableRooms
                ? format(loc(roomPanelShowingAvailableRoomsIn), locPlaceType, appliedFilters)
                : format(loc(roomPanelShowingRoomsIn), locPlaceType, appliedFilters);
        }

        return (
            <Label className={getClass('header')} id={roomResultsHeaderLabelId.current}>
                {roomResultsHeader}
            </Label>
        );
    };

    const renderRooms = (): JSX.Element => {
        let roomsToRender = getStore().roomListViewState.rooms;
        if (isFeatureEnabled('cal-mf-advancedRoomBookingV2')) {
            roomsToRender = roomsToRender
                ?.map(room => ({
                    ...room,
                    ...getRoomFreeBusy(
                        room.email,
                        props.freeBusyCacheManager,
                        props.calendarEvent,
                        room.bookingType
                    ),
                }))
                .sort((a, b) => {
                    if (
                        a.roomRecurrenceAvailability?.availableInstancesCount &&
                        b.roomRecurrenceAvailability?.availableInstancesCount
                    ) {
                        return (
                            b.roomRecurrenceAvailability.availableInstancesCount -
                            a.roomRecurrenceAvailability.availableInstancesCount
                        );
                    }
                    return a.freeBusyState - b.freeBusyState;
                });
        }
        getStore().roomFilterState?.selectedFilters?.forEach(
            filter => (roomsToRender = applySelectedFilters(filter, roomsToRender))
        );
        return (
            <>
                {(flights.advancedRoomBooking || flights.advancedRoomBookingV2) && renderHeader()}
                {roomsToRender?.map((item: Room) => (
                    <RoomResult
                        key={item.email}
                        room={item}
                        showOnlyAvailableRooms={props.roomFilters.showOnlyAvailableRooms}
                        freeBusyCacheManager={props.freeBusyCacheManager}
                        calendarEvent={props.calendarEvent}
                        colorScheme={props.colorScheme}
                        onRoomSelected={() => onRoomSelected(item)}
                    />
                ))}
                <NoRoomsMessage locPlaceType={locPlaceType} />
            </>
        );
    };

    const onRoomSelected = (room: Room) => {
        setSelectedRoomName(room.name);
        props.onRoomSelected(room);
        // if there's any pills selected in the room panel well, then the room is selected from search;
        // otherwise it's selected from LAS suggestion or default room list
        if (
            (props.roomFilters.cities && props.roomFilters.cities.length > 0) ||
            (props.roomFilters.roomLists && props.roomFilters.roomLists.length > 0)
        ) {
            hasSelectedFromSearch();
        } else {
            hasSelectedFromSuggestion();
        }
    };

    const shouldDisplayFilters =
        !flights.advancedRoomBookingV2 &&
        props.roomFilters &&
        ((props.roomFilters.cities && props.roomFilters.cities.length > 0) ||
            (props.roomFilters.roomLists && props.roomFilters.roomLists.length > 0)) &&
        flights.advancedRoomBooking &&
        getStore().roomListViewState.rooms &&
        getStore().roomListViewState.rooms.length > 0;
    return (
        <div className={getClass('filterableRoomList')}>
            {shouldDisplayFilters && (
                <CapabilityFilters
                    className={getClass('filterHeader')}
                    theme={themeFromColorScheme(props.colorScheme)}
                />
            )}
            {selectedRoomName && (
                <LiveNotification
                    message={format(loc(roomSelectionAnnouncement), selectedRoomName)}
                    atomic={true}
                />
            )}
            <div className={classNames(getClass('roomList'), getClass('roomListScrollable'))}>
                <FocusZone
                    direction={FocusZoneDirection.vertical}
                    role="list"
                    ariaLabelledBy={roomResultsHeaderLabelId.current}>
                    {!getStore().roomListViewState.isSearchingForRooms && renderRooms()}
                </FocusZone>
            </div>
        </div>
    );
});

function applyFeaturesFilters(rooms: Room[], featureFilters: FeatureFilterItemType[]): Room[] {
    featureFilters.forEach(feature => {
        switch (feature) {
            case FeatureFilterItemType.Audio:
                rooms = rooms.filter(room => room.audioDeviceName);
                break;
            case FeatureFilterItemType.Display:
                rooms = rooms.filter(room => room.displayDeviceName);
                break;
            case FeatureFilterItemType.Video:
                rooms = rooms.filter(room => room.videoDeviceName);
                break;
            case FeatureFilterItemType.Accessible:
                rooms = rooms.filter(room => room.isWheelChairAccessible);
                break;
            default:
                break;
        }
    });
    return rooms;
}

function applyTagFilters(rooms: Room[], tagFilters: string[]): Room[] {
    if (tagFilters.length == 0) {
        return rooms;
    }
    const tagsIndex = getStore().roomListViewState.tagsIndex;
    const roomEmails = new Set<string>();
    let taggedRooms: Room[] = [];
    tagFilters.forEach(tag => {
        taggedRooms = taggedRooms.concat(tagsIndex.get(tag).slice());
    });
    return taggedRooms.filter(room => {
        const isAlreadyProcessed = roomEmails.has(room.email);
        roomEmails.add(room.email);
        return !isAlreadyProcessed;
    });
}

function applySelectedFilters(filter: CapabilityFilter, filteredRoomList: Room[]): Room[] {
    const capabilities = getRoomlistCapabilities();
    switch (filter.filterName) {
        case CapabilityFilterType.Capacity:
            if (capabilities.maxCapacity === 0) {
                // The current room list does not support capacity filtering, clear and ignore
                updateCapacityValue(null);
                break;
            }
            // apply capacity filter, if the capacity of a room is null, it's considered as 0
            const capacityFilterValue = Number(
                removeCapacitySuffixIfNeeded(filter.filterValue as string)
            );
            if (!isNaN(capacityFilterValue)) {
                filteredRoomList = filteredRoomList.filter(
                    room => room.capacity >= capacityFilterValue
                );
            }
            break;
        case CapabilityFilterType.Feature:
            if (!capabilities.hasFeatures.includes(true)) {
                // The current room list does not support feature filtering, clear and ignore
                updateFeaturesValue([]);
                break;
            }
            filteredRoomList = applyFeaturesFilters(
                filteredRoomList,
                filter.filterValue as FeatureFilterItemType[]
            );
            break;
        case CapabilityFilterType.Floor:
            if (capabilities.minFloor === Infinity) {
                // The current room list does not support floor filtering, clear and ignore
                updateFloorValue(null);
                break;
            }
            filteredRoomList = filteredRoomList.filter(
                room => room.floor && room.floor == Number(filter.filterValue)
            );
            break;
        case CapabilityFilterType.Tag:
            if (!capabilities.hasTags) {
                // The current room list does not support tag filtering, clear and ignore
                updateTagsValue([]);
                break;
            }
            filteredRoomList = applyTagFilters(filteredRoomList, filter.filterValue as string[]);
            break;
        default:
            break;
    }
    return filteredRoomList;
}

const NoRoomsMessage = observer(function NoRoomsMessage(props: { locPlaceType: string }) {
    const { getClass } = useClientStyles(styles);
    // TODO VSO 86812 https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/86812
    // Once the empty state illustrations have been updated
    const SELECT_CALENDARITEM_SVG = isHostAppFeatureEnabled('defaultSelectCalendarItemSvg')
        ? require('../../svg/color/no_search_room_results.svg')
        : require('../../svg/color/no_search_room_results_opx.svg');
    const emptyStateIcon = getOwaResourceUrl(SELECT_CALENDARITEM_SVG);

    return (
        <EmptyState
            icon={emptyStateIcon}
            mainMessage={format(
                loc(noRoomsFoundMessageLineOne),
                props.locPlaceType.toLocaleLowerCase()
            )}
            detailMessage={loc(noRoomsFoundMessageLineTwo)}
            classNames={{
                container: getClass('noRoomResultsContainer'),
            }}
        />
    );
});
